/**
 * Google Ads Conversion Tracking Module
 * Main entry point for all tracking functionality
 * 
 * Usage:
 * import { initTracking, trackCalculatorInteraction, trackCtaClick } from './utils/tracking';
 * 
 * // Initialize on app mount
 * useEffect(() => {
 *   const cleanup = initTracking();
 *   return cleanup;
 * }, []);
 * 
 * // Track calculator changes
 * trackCalculatorInteraction('company_size', 'enterprise');
 * 
 * // Track CTA clicks
 * trackCtaClick('get_started', { quoteValue: 25000, platform: 'wordpress' });
 */

// Core utilities
export { pushEvent, sendGA4Event, getDataLayer } from './dataLayer.js';

// Attribution
export { 
  initAttribution, 
  getAttribution, 
  getAttributionValue,
  extractDomain,
  getPlatform,
} from './attribution.js';

// Event tracking
export {
  trackPageViewEnhanced,
  trackCalculatorInteraction,
  trackUrlAnalyze,
  trackCtaClick,
  trackFormStart,
  trackFormSubmit,
  trackFormSubmitSuccess,
  trackFormSubmitError,
  trackGenerateLead,
} from './events.js';

// Scroll tracking
export { initScrollTracking, resetScrollTracking } from './scroll.js';

// Engagement tracking
export { 
  initEngagementTracking, 
  markCalculatorInteraction,
  resetEngagementTracking,
  getEngagementState,
} from './engagement.js';

// Import for initialization
import { initAttribution } from './attribution.js';
import { initScrollTracking } from './scroll.js';
import { initEngagementTracking } from './engagement.js';
import { trackPageViewEnhanced } from './events.js';

/**
 * Initialize all tracking systems
 * Should be called once on app mount
 * @returns {Function} - Cleanup function to call on unmount
 */
export const initTracking = () => {
  try {
    // Initialize attribution capture first
    initAttribution();
    
    // Fire initial page view
    trackPageViewEnhanced();
    
    // Initialize scroll tracking
    const cleanupScroll = initScrollTracking();
    
    // Initialize engagement tracking
    const cleanupEngagement = initEngagementTracking();
    
    // Return combined cleanup function
    return () => {
      cleanupScroll();
      cleanupEngagement();
    };
  } catch (error) {
    console.error('[Tracking] Error initializing tracking:', error);
    return () => {};
  }
};

/**
 * Get all tracking state for debugging
 */
export const getTrackingState = () => {
  try {
    const { getAttribution } = require('./attribution.js');
    const { getEngagementState } = require('./engagement.js');
    const { getDataLayer } = require('./dataLayer.js');
    
    return {
      attribution: getAttribution(),
      engagement: getEngagementState(),
      dataLayerLength: getDataLayer().length,
    };
  } catch (error) {
    return { error: error.message };
  }
};

export default {
  initTracking,
  getTrackingState,
};
